import { AdTemplate } from '@/components/AdTemplate';
import { Layout } from '@/components/Layout';
import { PageMeta } from '@/components/PageMeta';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { Meta } from '@/types/content';
import { CommonPageProps } from '@/types/page';
import { getDynamicCacheTtl } from '@/utils/getDynamicCacheTtl';
import { isDeviceInvalid } from '@/utils/isDeviceInvalid';
import { joinify } from '@/utils/joinify';
import { MINUTE } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { withLockedContentHeaders } from '@/utils/withLockedContentHeaders';
import { withOcelotCacheTagsHeaders } from '@/utils/withOcelotCacheTagsHeaders';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { withTestContentHeaders } from '@/utils/withTestContentHeaders';
import { InfinityScrollFront } from 'base/components/InfinityScrollFront';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { getLabradorPage } from 'lib/labrador/getLabradorPage';
import { getLabradorPageProps, LabradorPageProps } from 'lib/labrador/getLabradorPageProps';
import { handleCommonLabradorPageStatus } from 'lib/labrador/handleCommonLabradorPageStatus';
import { renderPostTypeComponent } from 'modules/dynamic/components';
import { GetServerSideProps, NextPage } from 'next';

import { publisher } from 'base/components/standalone/SchemaMarkup/utils';
import { getLoggedInUserInfo } from 'lib/services/user';
import { clearAlltidDataCookie } from '@/utils/clearAlltidDataCookie';

const Page: NextPage<LabradorPageProps & CommonPageProps> = ({ pageData, adsData, analyticsData }) => {
  const infinityScrollFront = getOcelotConfig('features.infinityScrollFront');
  const infinityScrollId = pageData.meta.infinityScrollID;

  const showInfinityScrollFront =
    infinityScrollFront.enabled &&
    infinityScrollId &&
    !infinityScrollFront.excludedAccessLevels.includes(pageData.meta.accessLevel);

  const { title, seoTitle } = pageData?.data ?? {};
  const publisherName = publisher instanceof Object && 'name' in publisher ? publisher.name : (publisher as string);
  const pageTitle = joinify([publisherName, seoTitle || title], { separator: ' - ' });

  return (
    <Layout
      content={
        <>
          <PageMeta pageData={pageData} />
          <AdTemplate pageMeta={pageData?.meta as Meta} adsData={adsData} />
          <h1 className="sr-only">{pageTitle}</h1>
          <SchemaMarkup pageData={pageData} />
          {renderPostTypeComponent(pageData)}
          {showInfinityScrollFront && (
            <InfinityScrollFront pageData={pageData} sourceRoute={['_scroll', 'front', infinityScrollId].join('/')} />
          )}
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { device, params = ['index'] } = context.params as { device: string; params?: string[] };

  if (isDeviceInvalid(device)) {
    return { notFound: true };
  }

  const routeKey = params;

  const data = await getLabradorPage(device, routeKey, context);

  const commonLabradorResponse = handleCommonLabradorPageStatus(data);
  if (commonLabradorResponse) return commonLabradorResponse;

  const externalCacheTtl = getDynamicCacheTtl(data.pageData.children);

  withOcelotCacheTagsHeaders(context, data, externalCacheTtl, externalCacheTtl - 1 * MINUTE);
  withLockedContentHeaders(context, data);
  withTestContentHeaders(context, data);
  withCacheControlHeaders(context.res, externalCacheTtl);

  clearAlltidDataCookie(context);

  // For now only /mina-sidor needs user info
  const isMinaSidor = params.join('/') === 'mina-sidor';

  return withSerializationGuard({
    props: {
      ...getLabradorPageProps(data),
      userInfo: isMinaSidor ? await getLoggedInUserInfo(context) : null,
    },
  });
};

export default Page;
